'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const CLICK_ZOOM_SCALE = 2;
const DRAG_THRESHOLD_PX = 4;
const WHEEL_SENSITIVITY = 0.01;
const CLICK_ANIMATION_MS = 180;

interface Transform {
	scale: number;
	x: number;
	y: number;
}

const INITIAL: Transform = { scale: 1, x: 0, y: 0 };

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const clampOffset = (x: number, y: number, scale: number, w: number, h: number) => {
	const maxX = (w * (scale - 1)) / 2;
	const maxY = (h * (scale - 1)) / 2;
	return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
};

const computeZoom = (
	prev: Transform,
	clientX: number,
	clientY: number,
	targetScale: number,
	rect: { left: number; top: number; width: number; height: number },
): Transform => {
	const nextScale = clamp(targetScale, MIN_SCALE, MAX_SCALE);
	if (nextScale === prev.scale) return prev;
	if (nextScale <= 1) return INITIAL;
	const cx = rect.left + rect.width / 2;
	const cy = rect.top + rect.height / 2;
	const px = clientX - cx - prev.x;
	const py = clientY - cy - prev.y;
	const ratio = nextScale / prev.scale;
	const nx = prev.x + px * (1 - ratio);
	const ny = prev.y + py * (1 - ratio);
	const { x, y } = clampOffset(nx, ny, nextScale, rect.width, rect.height);
	return { scale: nextScale, x, y };
};

export interface ImageZoomApi {
	ref: RefObject<HTMLImageElement | null>;
	surfaceRef: RefObject<HTMLElement | null>;
	transform: Transform;
	isZoomed: boolean;
	isAnimating: boolean;
	onClick: (e: ReactMouseEvent<HTMLImageElement>) => void;
	reset: () => void;
}

/**
 * Pinch-to-zoom (touch + trackpad), click-to-zoom (mouse), and drag-to-pan when zoomed.
 * Pass a resetKey that changes when the underlying image source changes to reset state.
 */
export const useImageZoom = (resetKey: unknown): ImageZoomApi => {
	const ref = useRef<HTMLImageElement | null>(null);
	const surfaceRef = useRef<HTMLElement | null>(null);
	const [transform, setTransform] = useState<Transform>(INITIAL);
	const [isAnimating, setIsAnimating] = useState(false);

	const transformRef = useRef<Transform>(INITIAL);
	transformRef.current = transform;
	const suppressClickRef = useRef(false);
	const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const reset = useCallback(() => setTransform(INITIAL), []);

	const animateTo = useCallback((next: Transform) => {
		setIsAnimating(true);
		setTransform(next);
		if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
		animationTimerRef.current = setTimeout(() => setIsAnimating(false), CLICK_ANIMATION_MS);
	}, []);

	useEffect(() => {
		reset();
	}, [resetKey, reset]);

	useEffect(
		() => () => {
			if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
		},
		[],
	);

	const onClick = useCallback(
		(e: ReactMouseEvent<HTMLImageElement>) => {
			if (suppressClickRef.current) {
				suppressClickRef.current = false;
				return;
			}
			const el = ref.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const prev = transformRef.current;
			const target = prev.scale > 1 ? 1 : CLICK_ZOOM_SCALE;
			animateTo(computeZoom(prev, e.clientX, e.clientY, target, rect));
		},
		[animateTo],
	);

	// Wheel: ctrl/meta + wheel = trackpad pinch (zoom).
	// Plain wheel while zoomed = trackpad two-finger drag (pan).
	// Listens on surfaceRef if attached (e.g. the lightbox backdrop) so gestures
	// outside the image still pan; falls back to the image element.
	useEffect(() => {
		const el = surfaceRef.current ?? ref.current;
		if (!el) return;
		const handler = (e: WheelEvent) => {
			const imgEl = ref.current;
			if (!imgEl) return;
			const prev = transformRef.current;
			const rect = imgEl.getBoundingClientRect();
			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
				const factor = 1 - e.deltaY * WHEEL_SENSITIVITY;
				setTransform(computeZoom(prev, e.clientX, e.clientY, prev.scale * factor, rect));
				return;
			}
			if (prev.scale <= 1) return;
			e.preventDefault();
			const lineMultiplier = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;
			const dx = e.deltaX * lineMultiplier;
			const dy = e.deltaY * lineMultiplier;
			const { x, y } = clampOffset(prev.x - dx, prev.y - dy, prev.scale, rect.width, rect.height);
			setTransform({ ...prev, x, y });
		};
		el.addEventListener('wheel', handler, { passive: false });
		return () => el.removeEventListener('wheel', handler);
	}, []);

	// Touch: two-finger pinch + one-finger pan while zoomed
	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		let pinch: { dist: number; startScale: number } | null = null;
		let touchPan: { x: number; y: number; startX: number; startY: number; moved: boolean } | null = null;

		const dist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

		const onStart = (e: TouchEvent) => {
			if (e.touches.length === 2) {
				pinch = {
					dist: dist(e.touches[0], e.touches[1]),
					startScale: transformRef.current.scale,
				};
				touchPan = null;
			} else if (e.touches.length === 1 && transformRef.current.scale > 1) {
				touchPan = {
					x: e.touches[0].clientX,
					y: e.touches[0].clientY,
					startX: transformRef.current.x,
					startY: transformRef.current.y,
					moved: false,
				};
			}
		};
		const onMove = (e: TouchEvent) => {
			const rect = el.getBoundingClientRect();
			if (e.touches.length === 2 && pinch) {
				e.preventDefault();
				const [t1, t2] = [e.touches[0], e.touches[1]];
				const d = dist(t1, t2);
				if (pinch.dist === 0) return;
				const target = pinch.startScale * (d / pinch.dist);
				const midX = (t1.clientX + t2.clientX) / 2;
				const midY = (t1.clientY + t2.clientY) / 2;
				setTransform(computeZoom(transformRef.current, midX, midY, target, rect));
			} else if (e.touches.length === 1 && touchPan) {
				const dx = e.touches[0].clientX - touchPan.x;
				const dy = e.touches[0].clientY - touchPan.y;
				if (!touchPan.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) touchPan.moved = true;
				if (!touchPan.moved) return;
				e.preventDefault();
				const prev = transformRef.current;
				const { x, y } = clampOffset(
					touchPan.startX + dx,
					touchPan.startY + dy,
					prev.scale,
					rect.width,
					rect.height,
				);
				setTransform({ ...prev, x, y });
			}
		};
		const onEnd = (e: TouchEvent) => {
			if (e.touches.length < 2) pinch = null;
			if (e.touches.length === 0) touchPan = null;
		};

		el.addEventListener('touchstart', onStart, { passive: false });
		el.addEventListener('touchmove', onMove, { passive: false });
		el.addEventListener('touchend', onEnd);
		el.addEventListener('touchcancel', onEnd);
		return () => {
			el.removeEventListener('touchstart', onStart);
			el.removeEventListener('touchmove', onMove);
			el.removeEventListener('touchend', onEnd);
			el.removeEventListener('touchcancel', onEnd);
		};
		// resetKey changes when the active image element swaps (carousel), so
		// re-bind the listeners to the new ref.current.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetKey]);

	// Mouse / pen drag pan while zoomed
	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		let pan: {
			x: number;
			y: number;
			startX: number;
			startY: number;
			pointerId: number;
			moved: boolean;
		} | null = null;

		const onDown = (e: PointerEvent) => {
			if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
			if (transformRef.current.scale <= 1) return;
			pan = {
				x: e.clientX,
				y: e.clientY,
				startX: transformRef.current.x,
				startY: transformRef.current.y,
				pointerId: e.pointerId,
				moved: false,
			};
			try {
				el.setPointerCapture(e.pointerId);
			} catch {
				// ignore — capture is best-effort
			}
		};
		const onMove = (e: PointerEvent) => {
			if (!pan || e.pointerId !== pan.pointerId) return;
			const dx = e.clientX - pan.x;
			const dy = e.clientY - pan.y;
			if (!pan.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) pan.moved = true;
			if (!pan.moved) return;
			const rect = el.getBoundingClientRect();
			const prev = transformRef.current;
			const { x, y } = clampOffset(
				pan.startX + dx,
				pan.startY + dy,
				prev.scale,
				rect.width,
				rect.height,
			);
			setTransform({ ...prev, x, y });
		};
		const onUp = (e: PointerEvent) => {
			if (!pan || e.pointerId !== pan.pointerId) return;
			if (pan.moved) suppressClickRef.current = true;
			try {
				el.releasePointerCapture(e.pointerId);
			} catch {
				// ignore
			}
			pan = null;
		};

		el.addEventListener('pointerdown', onDown);
		el.addEventListener('pointermove', onMove);
		el.addEventListener('pointerup', onUp);
		el.addEventListener('pointercancel', onUp);
		return () => {
			el.removeEventListener('pointerdown', onDown);
			el.removeEventListener('pointermove', onMove);
			el.removeEventListener('pointerup', onUp);
			el.removeEventListener('pointercancel', onUp);
		};
		// resetKey changes when the active image element swaps (carousel), so
		// re-bind the listeners to the new ref.current.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetKey]);

	return {
		ref,
		surfaceRef,
		transform,
		isZoomed: transform.scale > 1,
		isAnimating,
		onClick,
		reset,
	};
};
